/**
 * Pure FareHarbor URL helpers. Kept framework-free + side-effect-free so
 * the renderer and the layout script stay trivial and testable.
 *
 * The exact lightframe script endpoint + booking query params are the
 * design intent; confirm against the tenant's own FareHarbor dashboard
 * "Get the code" output if behavior ever drifts — that dashboard is the
 * authoritative source.
 */

export type FareharborLayout = 'button' | 'inline'

/**
 * Sentinel CTA href that means "open the tenant's FareHarbor booking flow"
 * instead of navigating. A dealer's booking buttons (hero / cta_band /
 * booking_cta) use this in place of a real path so the storefront opens the
 * FareHarbor lightbox — the shortname comes from the tenant (single source of
 * truth), and an optional `?flow=` deep-links to a specific flow.
 *
 *   fareharbor:book              → tenant's full booking catalog
 *   fareharbor:book?flow=1524774 → a specific flow
 *
 * Returns the parsed `{ flow? }` for a sentinel href, or null for any normal
 * link (which renders as an ordinary anchor).
 */
export function parseFareharborCtaHref(
  href: string | null | undefined,
): { flow?: string } | null {
  if (!href || !href.startsWith('fareharbor:')) return null
  const qIdx = href.indexOf('?')
  if (qIdx === -1) return {}
  try {
    const flow = new URLSearchParams(href.slice(qIdx + 1)).get('flow') || undefined
    return { flow }
  } catch {
    return {}
  }
}

export function buildFareharborBookingUrl(args: {
  shortname: string
  layout: FareharborLayout
  flow?: string
}): string {
  const base = `https://fareharbor.com/embeds/book/${encodeURIComponent(args.shortname)}/`
  const params = ['full-items=yes']
  if (args.flow) params.push(`flow=${encodeURIComponent(args.flow)}`)
  if (args.layout === 'inline') params.push('fallback=simple')
  return `${base}?${params.join('&')}`
}

export function fareharborLightframeSrc(): string {
  return 'https://fareharbor.com/embeds/api/v1/?autolightframe=yes'
}

function isFareharborOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname
    return host === 'fareharbor.com' || host.endsWith('.fareharbor.com')
  } catch {
    return false
  }
}

/**
 * The `name` we put on an inline embed iframe. FareHarbor's app reads
 * `window.name` on load to learn it is an inline embed (`lightframed:false`,
 * vs the fullscreen lightframe overlay) and which `embedId` to stamp on the
 * resize messages it posts to the parent. Mirrors the shape FareHarbor's own
 * embed script produces. Must be set BEFORE the iframe's src loads.
 */
export function fareharborEmbedName(embedId: string, parentUrl: string): string {
  return (
    'fareharbor-embed:' +
    encodeURIComponent(JSON.stringify({ lightframed: false, parentUrl, embedId }))
  )
}

/**
 * Extract a content height from a FareHarbor `postMessage`, or null if the
 * message isn't a trustworthy resize for THIS embed. FareHarbor posts a
 * JSON string `{ type, embedId, height }` from its embed iframe; we resize
 * on any fareharbor-origin message that carries a positive height tagged
 * with our embedId (we don't depend on the exact `type` string, which is
 * undocumented and could change). Origin + embedId checks prevent a hostile
 * frame or a sibling embed from driving this iframe's height.
 */
export function fareharborHeightFromMessage(args: {
  origin: string
  data: unknown
  embedId: string
}): number | null {
  if (!isFareharborOrigin(args.origin)) return null
  let payload: unknown = args.data
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch {
      return null
    }
  }
  if (!payload || typeof payload !== 'object') return null
  const record = payload as Record<string, unknown>
  if (record.embedId !== args.embedId) return null
  const height = record.height
  return typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : null
}
